import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Spinner, Collapse, Button } from "reactstrap"
import axios from "axios"
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
} from "recharts"
import { setBreadcrumbItems } from "../../../store/actions"
import { getErroresSummary, getErroresList, getResumenEmisor, reenviarCorreo } from "../api"
import {
  formatNumber, formatPercent, formatFecha, formatFechaHora,
  bounceConfig, colorPara, rangoDelMes, buildRangoTimestamp,
} from "../helpers"
import HeaderFiltros from "../Analisis/HeaderFiltros"
import DrawerDetalle from "./DrawerDetalle"

const breadcrumbItems = [
  { title: "LS-DA", link: "#" },
  { title: "Envío de correos", link: "#" },
  { title: "Errores por Emisor", link: "#" },
]

const ahora = new Date()
const PAGE_SIZE_OPTS = [25, 50, 100, 200, 500]

const ETAPA_COLOR = {
  email: "#dc2626",
  pdf: "#f59e0b",
  download: "#3b82f6",
  attached_doc: "#8b5cf6",
  zip: "#06b6d4",
  redis_read: "#10b981",
  unexpected: "#ec4899",
  validation: "#84cc16",
  unknown: "#64748b",
}
const colorEtapa = e => ETAPA_COLOR[String(e || "").toLowerCase()] || "#64748b"

// ───────── UI helpers ─────────

const StatCard = ({ icon, label, value, accent = "#3b82f6", sub }) => (
  <div style={{
    flex: "1 1 200px", minWidth: 0,
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14,
    padding: "16px 20px",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: `${accent}1a`, color: accent,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 18, flexShrink: 0,
      }}>
        <i className={`mdi ${icon}`} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </span>
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{sub}</div>}
  </div>
)
StatCard.propTypes = { icon: PropTypes.string, label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node }

const SectionCard = ({ title, icon, children, action, onClick, collapsed }) => (
  <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
    <div
      className="d-flex justify-content-between align-items-baseline mb-2 flex-wrap gap-2"
      onClick={onClick}
      style={onClick ? { cursor: "pointer", userSelect: "none" } : undefined}
    >
      <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
        {onClick && (
          <i className={`mdi mdi-chevron-${collapsed ? "right" : "down"} me-1`} style={{ color: "#94a3b8" }} />
        )}
        {icon && <i className={`mdi ${icon} me-2 text-primary`} />}
        {title}
      </h6>
      {action}
    </div>
    {children}
  </div>
)
SectionCard.propTypes = { title: PropTypes.node, icon: PropTypes.string, children: PropTypes.node, action: PropTypes.node, onClick: PropTypes.func, collapsed: PropTypes.bool }

const th = {
  background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
  padding: "8px 10px", textAlign: "left",
  fontSize: 11, color: "#475569", fontWeight: 700,
  textTransform: "uppercase", letterSpacing: 0.4,
}
const td = {
  padding: "8px 10px", borderBottom: "1px solid #f1f5f9",
  fontSize: 12, color: "#0f172a", verticalAlign: "middle",
}

// ───────── Toolbar de filtros avanzados ─────────

const FiltrosAvanzados = ({ filtros, setFiltros, etapasDisponibles, bouncesDisponibles, onAplicar, loading }) => {
  const inputBase = { fontSize: 12, padding: "5px 9px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", height: 32 }
  const set = (k, v) => setFiltros(prev => ({ ...prev, [k]: v }))
  const onEnter = (e) => { if (e.key === "Enter") onAplicar() }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
      <select style={{ ...inputBase, minWidth: 140 }} value={filtros.error_stage || ""}
        onChange={e => set("error_stage", e.target.value)}>
        <option value="">Etapa: Todas</option>
        {etapasDisponibles.map(et => <option key={et} value={et}>{et}</option>)}
      </select>
      <select style={{ ...inputBase, minWidth: 150 }} value={filtros.bounce_type || ""}
        onChange={e => set("bounce_type", e.target.value)}>
        <option value="">Bounce: Todos</option>
        {bouncesDisponibles.map(bt => <option key={bt} value={bt}>{bounceConfig(bt).label}</option>)}
      </select>
      <select style={{ ...inputBase, minWidth: 120 }} value={filtros.source || ""}
        onChange={e => set("source", e.target.value)}>
        <option value="">Origen: Todos</option>
        <option value="listener">listener</option>
        <option value="batch">batch</option>
        <option value="retry">retry</option>
        <option value="manual">manual</option>
      </select>
      <input type="text" placeholder="NIT receptor" style={{ ...inputBase, width: 130 }}
        value={filtros.nit_receptor || ""} onChange={e => set("nit_receptor", e.target.value)} onKeyDown={onEnter} />
      <input type="text" placeholder="Email destinatario" style={{ ...inputBase, width: 210 }}
        value={filtros.recipient || ""} onChange={e => set("recipient", e.target.value)} onKeyDown={onEnter} />
      <input type="text" placeholder="Prefijo (FE, NC...)" style={{ ...inputBase, width: 130 }}
        value={filtros.prefijo || ""} onChange={e => set("prefijo", e.target.value)} onKeyDown={onEnter} />
      <input type="text" placeholder="CUFE" style={{ ...inputBase, width: 180 }}
        value={filtros.cufe || ""} onChange={e => set("cufe", e.target.value)} onKeyDown={onEnter} />
      <button type="button" className="btn btn-sm btn-primary" onClick={() => onAplicar()} disabled={loading}
        style={{ height: 32, padding: "0 14px", fontSize: 12, fontWeight: 600 }}>
        {loading ? <><Spinner size="sm" /> Buscando…</> : <><i className="mdi mdi-filter-check me-1" /> Aplicar</>}
      </button>
      <button type="button" className="btn btn-sm btn-outline-secondary"
        onClick={() => { setFiltros({}); onAplicar({}) }}
        style={{ height: 32, padding: "0 12px", fontSize: 12 }}>
        <i className="mdi mdi-filter-remove me-1" /> Limpiar
      </button>
    </div>
  )
}
FiltrosAvanzados.propTypes = {
  filtros: PropTypes.object.isRequired,
  setFiltros: PropTypes.func.isRequired,
  etapasDisponibles: PropTypes.array,
  bouncesDisponibles: PropTypes.array,
  onAplicar: PropTypes.func.isRequired,
  loading: PropTypes.bool,
}

// ───────── Componente principal ─────────

const ErroresPorEmisor = ({ empresaSeleccionada, user, setBreadcrumbItems: setBreadcrumb }) => {
  const [modo, setModo] = useState("mes")
  const [year, setYear] = useState(ahora.getFullYear())
  const [month, setMonth] = useState(ahora.getMonth() + 1)
  const [fechaInicio, setFechaInicio] = useState("")
  const [fechaFin, setFechaFin] = useState("")

  const [summary, setSummary] = useState(null)
  const [loadingSummary, setLoadingSummary] = useState(false)
  const [errorSummary, setErrorSummary] = useState("")

  const [universo, setUniverso] = useState(null) // /a7/emisores/:nit/resumen → para % de CUFEs con error

  const [filtrosAvanzados, setFiltrosAvanzados] = useState({})
  const [filtrosAplicados, setFiltrosAplicados] = useState({})

  const [listaErrores, setListaErrores] = useState([])
  const [listaMeta, setListaMeta] = useState({ total: 0, limit: 50, offset: 0 })
  const [loadingLista, setLoadingLista] = useState(false)
  const [errorLista, setErrorLista] = useState("")
  const [pageSize, setPageSize] = useState(50)
  const [page, setPage] = useState(0)

  const [drawerAbierto, setDrawerAbierto] = useState(false)
  const [cufeActivo, setCufeActivo] = useState(null)
  const [consultado, setConsultado] = useState(false)

  // Toggles colapsables
  const [showSucursales, setShowSucursales] = useState(false)
  const [showComunes, setShowComunes] = useState(false)

  // Vista de árbol agrupada por receptor (default on)
  const [agruparPorReceptor, setAgruparPorReceptor] = useState(true)
  const [expandedReceptores, setExpandedReceptores] = useState(() => new Set())

  const toggleReceptor = (nit) => {
    setExpandedReceptores(prev => {
      const next = new Set(prev)
      if (next.has(nit)) next.delete(nit)
      else next.add(nit)
      return next
    })
  }
  const expandAll = (nits) => setExpandedReceptores(new Set(nits))
  const collapseAll = () => setExpandedReceptores(new Set())

  // ───────── Selección masiva para reenvío ─────────
  const [selectedCufes, setSelectedCufes] = useState(() => new Set())
  const [bulkMail, setBulkMail] = useState("")
  const [bulkSending, setBulkSending] = useState(false)
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, ok: 0, fail: 0, errors: [] })
  const [bulkSummary, setBulkSummary] = useState(null) // resultado final tras reenvío

  const toggleCufe = (cufe) => {
    if (!cufe) return
    setSelectedCufes(prev => {
      const next = new Set(prev)
      if (next.has(cufe)) next.delete(cufe)
      else next.add(cufe)
      return next
    })
  }
  const toggleGroupCufes = (cufes) => {
    setSelectedCufes(prev => {
      const next = new Set(prev)
      const allSelected = cufes.length > 0 && cufes.every(c => next.has(c))
      if (allSelected) cufes.forEach(c => next.delete(c))
      else cufes.forEach(c => c && next.add(c))
      return next
    })
  }
  const clearSelection = () => { setSelectedCufes(new Set()); setBulkMail(""); setBulkSummary(null) }

  // Limpia la selección al cambiar de página/filtros para evitar mantener selecciones invisibles
  useEffect(() => { setSelectedCufes(new Set()); setBulkSummary(null) }, [page, filtrosAplicados, pageSize])

  const nitemisor = empresaSeleccionada?.nit || empresaSeleccionada?.identificacion || ""
  const empresaNombre = empresaSeleccionada?.nombre || ""

  useEffect(() => {
    document.title = "Errores por emisor | LS-DA"
    const greeting = user
      ? `Bienvenido, ${user.nombre || ""} ${user.apellido || ""}`.trim()
      : "Errores por emisor"
    setBreadcrumb(greeting, breadcrumbItems)
  }, [user, setBreadcrumb])

  useEffect(() => {
    setSummary(null)
    setUniverso(null)
    setListaErrores([])
    setListaMeta({ total: 0, limit: 50, offset: 0 })
    setConsultado(false)
    setErrorSummary("")
    setErrorLista("")
    setPage(0)
    setFiltrosAvanzados({})
    setFiltrosAplicados({})
    setShowSucursales(false)
    setShowComunes(false)
  }, [nitemisor])

  const construirRango = useCallback(() => {
    if (modo === "mes") {
      const { desde, hasta } = rangoDelMes(year, month)
      return { desde, hasta }
    }
    return buildRangoTimestamp(fechaInicio, fechaFin)
  }, [modo, year, month, fechaInicio, fechaFin])

  const puedeConsultar = useMemo(() => {
    if (!nitemisor) return false
    if (modo === "mes") return Boolean(year && month)
    return Boolean(fechaInicio && fechaFin && fechaInicio <= fechaFin)
  }, [nitemisor, modo, year, month, fechaInicio, fechaFin])

  // Carga del summary (acepta error_stage y nit_receptor del back; el resto se ignoran server-side)
  const cargarSummary = useCallback(async (filtros = {}) => {
    if (!nitemisor) return
    setLoadingSummary(true)
    setErrorSummary("")
    try {
      const rango = construirRango()
      const r = await getErroresSummary({
        nitemisor, ...rango,
        error_stage: filtros.error_stage || undefined,
        nit_receptor: filtros.nit_receptor || undefined,
      })
      const body = r.data
      if (body?.success === false) throw new Error(body.error || body.message || "Error en el servidor")
      // El back devuelve el summary al nivel raíz (no envuelto en `data`)
      setSummary(body)
      setConsultado(true)
    } catch (err) {
      setErrorSummary(err.response?.data?.error || err.message || "No se pudo cargar el resumen.")
      setSummary(null)
    } finally {
      setLoadingSummary(false)
    }
  }, [nitemisor, construirRango])

  // Universo total de correos del emisor (para calcular % CUFEs con error)
  const cargarUniverso = useCallback(async () => {
    if (!nitemisor) return
    try {
      const rango = construirRango()
      const r = await getResumenEmisor(nitemisor, rango)
      setUniverso(r.data?.data || r.data)
    } catch {
      setUniverso(null)
    }
  }, [nitemisor, construirRango])

  const cargarLista = useCallback(async (filtros, pg) => {
    if (!nitemisor) return
    setLoadingLista(true)
    setErrorLista("")
    try {
      const rango = construirRango()
      const r = await getErroresList({
        nitemisor, ...rango,
        ...filtros,
        limit: pageSize,
        offset: pg * pageSize,
      })
      const body = r.data
      if (body?.success === false) throw new Error(body.error || body.message || "Error en el servidor")
      // El back devuelve { success, total, offset, limit, count, data: [...] } a nivel raíz
      setListaErrores(body?.data || [])
      setListaMeta({
        total: body?.total ?? 0,
        limit: body?.limit ?? pageSize,
        offset: body?.offset ?? pg * pageSize,
      })
    } catch (err) {
      setErrorLista(err.response?.data?.error || err.message || "No se pudo cargar el listado.")
      setListaErrores([])
      setListaMeta({ total: 0, limit: pageSize, offset: 0 })
    } finally {
      setLoadingLista(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nitemisor, construirRango, pageSize])

  const handleConsultar = (overrideFiltros) => {
    const f = overrideFiltros !== undefined ? overrideFiltros : filtrosAvanzados
    setFiltrosAplicados(f)
    setPage(0)
    cargarUniverso()
    cargarSummary(f)
    cargarLista(f, 0)
  }

  const handlePageChange = (newPage) => {
    setPage(newPage)
    cargarLista(filtrosAplicados, newPage)
  }

  const handleAbrirDetalle = (cufe) => {
    setCufeActivo(cufe)
    setDrawerAbierto(true)
  }

  // Reenvío individual con fallback al endpoint legacy si /a7 responde 404
  const reenviarConFallback = async (cufe, mailReceptor) => {
    try {
      await reenviarCorreo({ cufe, mailReceptor })
      return { ok: true, legacy: false }
    } catch (err) {
      if (err.response?.status === 404) {
        // Fallback: el CUFE no está en el inbox del nuevo servicio
        await axios.post(
          "https://timbrador.ls-da.com/v2/factura/resendMail",
          { cufe, mail: mailReceptor || undefined },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: "eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJzb2Z0bG9neSIsImF1ZGllbmNlIjoid2ViIiwiY3JlYXRlZCI6MTczMjIxMTc5MTEyMCwiZXhwIjoyNzMyMjExNzkwfQ.q6_8G2cViYb3l7NU5R9gAWNo1JQz-afRkc-mPnuc9n2iIYUJlbq6XHzm-K_0ldnQmmkxFbRofiXkVAB41_Me5Q",
            },
          },
        )
        return { ok: true, legacy: true }
      }
      throw err
    }
  }

  const handleBulkReenviar = async () => {
    if (selectedCufes.size === 0 || bulkSending) return
    const cufes = Array.from(selectedCufes)
    const mail = bulkMail.trim()
    const confirmTxt = mail
      ? `Vas a reenviar ${cufes.length} factura${cufes.length > 1 ? "s" : ""} al correo "${mail}". ¿Continuar?`
      : `Vas a reenviar ${cufes.length} factura${cufes.length > 1 ? "s" : ""} a su(s) destinatario(s) original(es). ¿Continuar?`
    // eslint-disable-next-line no-alert
    if (!window.confirm(confirmTxt)) return

    setBulkSending(true)
    setBulkSummary(null)
    setBulkProgress({ current: 0, total: cufes.length, ok: 0, fail: 0, errors: [] })

    let ok = 0
    let fail = 0
    let legacyCount = 0
    const errores = []

    for (let i = 0; i < cufes.length; i++) {
      const cufe = cufes[i]
      try {
        const res = await reenviarConFallback(cufe, mail)
        ok++
        if (res.legacy) legacyCount++
      } catch (err) {
        fail++
        errores.push({
          cufe,
          msg: err.response?.data?.error?.message
            || err.response?.data?.message
            || err.response?.data?.error
            || err.message
            || "Error desconocido",
        })
      }
      setBulkProgress({ current: i + 1, total: cufes.length, ok, fail, errors: errores })
    }

    setBulkSending(false)
    setBulkSummary({ total: cufes.length, ok, fail, legacy: legacyCount, errores })
    if (ok > 0) {
      // Refresca el listado para reflejar el nuevo intento
      cargarLista(filtrosAplicados, page)
      // Mantenemos la selección por si el usuario quiere ver el resultado;
      // se limpia cuando cierra el resumen.
    }
  }

  // ───────── Derivar datos del summary ─────────

  const totales = summary?.totales || {}
  const porEtapa = useMemo(() => summary?.por_etapa || [], [summary])
  const porBounce = useMemo(() => summary?.por_bounce_type || [], [summary])
  const porOrigen = useMemo(() => summary?.por_origen_proceso || [], [summary])
  const tendencia = useMemo(() => summary?.tendencia_diaria || [], [summary])
  const topReceptores = useMemo(() => summary?.top_nit_receptores || [], [summary])
  const topDestinatarios = useMemo(() => summary?.top_destinatarios || [], [summary])
  const topSucursales = useMemo(() => summary?.top_sucursales || [], [summary])
  const erroresComunes = useMemo(() => summary?.errores_mas_comunes || [], [summary])

  // Etapas dinámicas (para el filtro dropdown)
  const etapasDisponibles = useMemo(() => {
    const set = new Set()
    porEtapa.forEach(e => { if (e.error_stage) set.add(e.error_stage) })
    tendencia.forEach(d => Object.keys(d).forEach(k => {
      if (!["dia", "total", "fecha"].includes(k)) set.add(k)
    }))
    return Array.from(set)
  }, [porEtapa, tendencia])

  const bouncesDisponibles = useMemo(
    () => Array.from(new Set(porBounce.map(b => b.bounce_type).filter(Boolean))),
    [porBounce],
  )

  // Filtramos los registros sin nit_receptor para no mostrarlos en el listado.
  // El conteo de omitidos se expone en la cabecera de la tabla.
  const listaErroresFiltrada = useMemo(
    () => listaErrores.filter(e => e.nit_receptor),
    [listaErrores],
  )
  const omitidosSinNit = listaErrores.length - listaErroresFiltrada.length

  // Agrupación por receptor (NIT) para vista de árbol
  const erroresAgrupados = useMemo(() => {
    const groups = new Map()
    for (const e of listaErroresFiltrada) {
      const key = e.nit_receptor || "__SIN_NIT__"
      if (!groups.has(key)) {
        groups.set(key, {
          nit: e.nit_receptor || "",
          nombre: e.nombre_receptor || "",
          items: [],
          cufesUnicos: new Set(),
          etapas: new Set(),
          bounces: new Set(),
        })
      }
      const g = groups.get(key)
      g.items.push(e)
      if (e.cufe) g.cufesUnicos.add(e.cufe)
      if (e.error_stage) g.etapas.add(e.error_stage)
      if (e.bounce_type) g.bounces.add(e.bounce_type)
      // Si no teníamos nombre pero esta fila sí, lo guardamos
      if (!g.nombre && e.nombre_receptor) g.nombre = e.nombre_receptor
    }
    // Ordenar por cantidad de errores DESC
    return Array.from(groups.entries())
      .map(([key, g]) => ({ ...g, key, cufesCount: g.cufesUnicos.size }))
      .sort((a, b) => b.items.length - a.items.length)
  }, [listaErroresFiltrada])

  // % CUFEs con error vs CUFEs totales del período
  const pctCufesConError = useMemo(() => {
    const cufesAfect = Number(totales.cufes_afectados) || 0
    const cufesTotal = Number(universo?.tarjetas?.total_correos) || 0
    if (!cufesTotal) return null
    return (cufesAfect / cufesTotal) * 100
  }, [totales.cufes_afectados, universo])

  if (!nitemisor) {
    return (
      <div style={{ background: "#fff", borderRadius: 14, padding: 60, textAlign: "center", border: "1px dashed #cbd5e1" }}>
        <i className="mdi mdi-domain-off" style={{ fontSize: 56, color: "#cbd5e1" }} />
        <h5 className="mt-3" style={{ color: "#0f172a" }}>Selecciona una empresa</h5>
        <p style={{ color: "#64748b", maxWidth: 420, margin: "8px auto 0" }}>
          Usa el selector <strong>“Seleccionar empresa”</strong> arriba a la derecha para ver los errores de envío.
        </p>
      </div>
    )
  }

  const totalPages = Math.max(1, Math.ceil((listaMeta.total || 0) / pageSize))
  const filtrosActivosCount = Object.values(filtrosAplicados).filter(v => v != null && v !== "").length

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* ─── Header + Toolbar unificado de filtros ─── */}
      <div style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
          <div>
            <div style={{ fontSize: 12, color: "#dc2626", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }}>
              <i className="mdi mdi-alert-circle me-1" />
              Envío de correos
            </div>
            <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#0f172a" }}>
              Errores por emisor
            </h3>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              <i className="mdi mdi-domain me-1 text-primary" />
              <strong>{empresaNombre}</strong> · NIT {nitemisor}
            </div>
          </div>
          {filtrosActivosCount > 0 && (
            <span style={{
              padding: "4px 12px", borderRadius: 999,
              background: "#dbeafe", color: "#1e40af",
              fontSize: 12, fontWeight: 700,
            }}>
              <i className="mdi mdi-filter-variant me-1" />
              {filtrosActivosCount} filtro{filtrosActivosCount !== 1 ? "s" : ""} activo{filtrosActivosCount !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Fila 1: rango */}
        <div className="mb-2">
          <HeaderFiltros
            modo={modo} setModo={setModo}
            year={year} setYear={setYear} month={month} setMonth={setMonth}
            fechaInicio={fechaInicio} setFechaInicio={setFechaInicio}
            fechaFin={fechaFin} setFechaFin={setFechaFin}
            loading={loadingSummary || loadingLista}
            onConsultar={() => handleConsultar()}
            puedeConsultar={puedeConsultar}
          />
        </div>

        {/* Fila 2: filtros avanzados (sólo visibles tras 1ra consulta) */}
        {consultado && (
          <div style={{
            marginTop: 12, paddingTop: 12,
            borderTop: "1px dashed #e2e8f0",
          }}>
            <FiltrosAvanzados
              filtros={filtrosAvanzados} setFiltros={setFiltrosAvanzados}
              etapasDisponibles={etapasDisponibles}
              bouncesDisponibles={bouncesDisponibles}
              onAplicar={(override) => handleConsultar(override)}
              loading={loadingSummary || loadingLista}
            />
          </div>
        )}
      </div>

      {errorSummary && (
        <div style={{
          padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca",
          color: "#991b1b", borderRadius: 10, display: "flex", alignItems: "center", gap: 10,
        }}>
          <i className="mdi mdi-alert-circle" style={{ fontSize: 20 }} />
          <span>{errorSummary}</span>
          <button type="button" className="btn btn-sm btn-outline-danger ms-auto"
            onClick={() => cargarSummary(filtrosAplicados)} disabled={loadingSummary}>Reintentar</button>
        </div>
      )}

      {!summary && loadingSummary && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 80, textAlign: "center", color: "#64748b", border: "1px solid #e2e8f0" }}>
          <Spinner color="primary" />
          <div className="mt-2">Cargando resumen…</div>
        </div>
      )}

      {!summary && !loadingSummary && !errorSummary && !consultado && (
        <div style={{ background: "#fff", borderRadius: 14, padding: "60px 40px", textAlign: "center", border: "1px dashed #cbd5e1" }}>
          <i className="mdi mdi-alert-octagon-outline" style={{ fontSize: 56, color: "#cbd5e1" }} />
          <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>
            Configura el período y consulta
          </h5>
          <p style={{ color: "#64748b", maxWidth: 460, margin: "8px auto 0", fontSize: 14 }}>
            Indica mes/año o rango de fechas para ver el resumen de errores de envío de <strong>{empresaNombre}</strong>.
          </p>
        </div>
      )}

      {summary && (
        <>
          {/* ─── 4 KPIs ─── */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <StatCard icon="mdi-alert-circle" label="Total errores"
              value={formatNumber(totales.total_errores)} accent="#dc2626" />
            <StatCard icon="mdi-file-alert-outline" label="CUFEs afectados"
              value={formatNumber(totales.cufes_afectados)} accent="#f59e0b" />
            <StatCard icon="mdi-account-alert-outline" label="Destinatarios afectados"
              value={formatNumber(totales.destinatarios_afectados)} accent="#8b5cf6" />
            <StatCard icon="mdi-percent-outline" label="% CUFEs con error"
              value={pctCufesConError != null ? formatPercent(pctCufesConError, 2) : "—"}
              accent={pctCufesConError != null && pctCufesConError >= 10 ? "#dc2626" : "#06b6d4"}
              sub={universo?.tarjetas?.total_correos
                ? `sobre ${formatNumber(universo.tarjetas.total_correos)} CUFEs totales del período`
                : "Sin total disponible para el cálculo"} />
          </div>

          {/* ─── 3 donuts (sin tipoPdf) ─── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
            {porEtapa.length > 0 && (
              <SectionCard title="Por etapa" icon="mdi-chart-donut">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={porEtapa} dataKey="cantidad" nameKey="error_stage"
                      innerRadius={45} outerRadius={80} paddingAngle={2}>
                      {porEtapa.map((e, i) => <Cell key={i} fill={colorEtapa(e.error_stage)} />)}
                    </Pie>
                    <Tooltip formatter={v => formatNumber(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </SectionCard>
            )}

            {porBounce.length > 0 && (
              <SectionCard title="Por tipo de bounce" icon="mdi-chart-donut">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={porBounce} dataKey="cantidad" nameKey="bounce_type"
                      innerRadius={45} outerRadius={80} paddingAngle={2}>
                      {porBounce.map((b, i) => <Cell key={i} fill={bounceConfig(b.bounce_type).color || colorPara(i)} />)}
                    </Pie>
                    <Tooltip formatter={(v, _, p) => [formatNumber(v), bounceConfig(p.payload.bounce_type).label]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} formatter={(value) => bounceConfig(value).label} />
                  </PieChart>
                </ResponsiveContainer>
              </SectionCard>
            )}

            {porOrigen.length > 0 && (
              <SectionCard title="Por origen del proceso" icon="mdi-chart-donut">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={porOrigen} dataKey="cantidad" nameKey="source"
                      innerRadius={45} outerRadius={80} paddingAngle={2}>
                      {porOrigen.map((_, i) => <Cell key={i} fill={colorPara(i)} />)}
                    </Pie>
                    <Tooltip formatter={v => formatNumber(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </SectionCard>
            )}
          </div>

          {/* ─── Tendencia diaria (líneas por etapa) ─── */}
          {tendencia.length > 0 && (
            <SectionCard title="Tendencia diaria por etapa" icon="mdi-chart-line">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={tendencia}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="dia" tick={{ fontSize: 11 }} tickFormatter={formatFecha} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={l => formatFecha(l)} formatter={v => formatNumber(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="total" name="Total" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 3 }} />
                  {etapasDisponibles.map((et) => (
                    <Line key={et} type="monotone" dataKey={et} name={et}
                      stroke={colorEtapa(et)} strokeWidth={1.5} dot={{ r: 2 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </SectionCard>
          )}

          {/* ─── 2 tablas TOP siempre visibles (receptores + destinatarios) ─── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 12 }}>
            {topReceptores.length > 0 && (
              <SectionCard title="Top receptores (clientes externos)" icon="mdi-domain">
                <div style={{ overflowX: "auto", maxHeight: 320, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={th}>NIT</th>
                        <th style={th}>Nombre</th>
                        <th style={{ ...th, textAlign: "right" }}>Errores</th>
                        <th style={{ ...th, textAlign: "right" }}>CUFEs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topReceptores.map((r, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                          <td style={{ ...td, fontFamily: "monospace" }}>{r.nit_receptor || "—"}</td>
                          <td style={td}>{r.nombre_receptor || "—"}</td>
                          <td style={{ ...td, textAlign: "right", color: "#dc2626", fontWeight: 700 }}>
                            {formatNumber(r.errores)}
                          </td>
                          <td style={{ ...td, textAlign: "right", color: "#475569" }}>
                            {formatNumber(r.cufes_afectados)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            )}

            {topDestinatarios.length > 0 && (
              <SectionCard title="Top destinatarios problemáticos" icon="mdi-account-alert-outline">
                <div style={{ overflowX: "auto", maxHeight: 320, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={th}>Email</th>
                        <th style={{ ...th, textAlign: "right" }}>Rebotes</th>
                        <th style={{ ...th, textAlign: "right" }}>CUFEs</th>
                        <th style={th}>Tipos bounce</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topDestinatarios.map((d, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                          <td style={{ ...td, wordBreak: "break-all" }}>{d.recipient || "—"}</td>
                          <td style={{ ...td, textAlign: "right", color: "#dc2626", fontWeight: 700 }}>
                            {formatNumber(d.errores)}
                          </td>
                          <td style={{ ...td, textAlign: "right", color: "#475569" }}>
                            {formatNumber(d.cufes_afectados)}
                          </td>
                          <td style={td}>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                              {(d.bounce_types || []).map((bt, idx) => {
                                const cfg = bounceConfig(bt)
                                return (
                                  <span key={idx} style={{
                                    padding: "1px 7px", borderRadius: 4, fontSize: 9, fontWeight: 700,
                                    background: cfg.bg, color: cfg.color,
                                  }}>{cfg.label}</span>
                                )
                              })}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            )}
          </div>

          {/* ─── Colapsable: Top sucursales ─── */}
          {topSucursales.length > 0 && (
            <SectionCard
              title={`Top sucursales con errores (${topSucursales.length})`}
              icon="mdi-source-branch"
              onClick={() => setShowSucursales(s => !s)}
              collapsed={!showSucursales}
            >
              <Collapse isOpen={showSucursales}>
                <div style={{ overflowX: "auto", maxHeight: 320, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={th}>Sucursal</th>
                        <th style={{ ...th, textAlign: "right" }}>Errores</th>
                        <th style={{ ...th, textAlign: "right" }}>CUFEs afectados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topSucursales.map((s, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                          <td style={{ ...td, fontWeight: 600 }}>{s.sucursal || "—"}</td>
                          <td style={{ ...td, textAlign: "right", color: "#dc2626", fontWeight: 700 }}>
                            {formatNumber(s.errores)}
                          </td>
                          <td style={{ ...td, textAlign: "right", color: "#475569" }}>
                            {formatNumber(s.cufes_afectados)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Collapse>
            </SectionCard>
          )}

          {/* ─── Colapsable: Errores más comunes ─── */}
          {erroresComunes.length > 0 && (
            <SectionCard
              title={`Errores más comunes (${erroresComunes.length})`}
              icon="mdi-bug-outline"
              onClick={() => setShowComunes(c => !c)}
              collapsed={!showComunes}
            >
              <Collapse isOpen={showComunes}>
                <div style={{ maxHeight: 480, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                  {erroresComunes.map((e, i) => {
                    const cfgB = bounceConfig(e.bounce_type)
                    return (
                      <div key={i} style={{
                        background: "#fef2f2", border: "1px solid #fecaca",
                        borderLeft: "3px solid #dc2626",
                        borderRadius: 6, padding: "10px 12px", fontSize: 12,
                      }}>
                        <div className="d-flex justify-content-between align-items-baseline mb-1 flex-wrap gap-2">
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <span style={{
                              padding: "1px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                              background: `${colorEtapa(e.error_stage)}1a`, color: colorEtapa(e.error_stage),
                            }}>{e.error_stage || "—"}</span>
                            {e.bounce_type && (
                              <span style={{
                                padding: "1px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                                background: cfgB.bg, color: cfgB.color,
                              }}>{cfgB.label}</span>
                            )}
                          </div>
                          <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#475569" }}>
                            <span><strong style={{ color: "#dc2626" }}>×{formatNumber(e.cantidad)}</strong></span>
                            <span>{formatNumber(e.cufes_afectados)} CUFEs</span>
                            <span>{formatNumber(e.destinatarios_afectados)} destinatarios</span>
                          </div>
                        </div>
                        <div style={{ color: "#7f1d1d", wordBreak: "break-word", fontFamily: "monospace", fontSize: 11 }}>
                          {e.ejemplo || "—"}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </Collapse>
            </SectionCard>
          )}

          {/* ─── Tabla principal de errores ─── */}
          <SectionCard
            title={
              <>
                Listado de errores · {formatNumber(listaMeta.total)}
                {filtrosActivosCount > 0 && (
                  <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: 8, fontWeight: 400 }}>
                    (con filtros)
                  </span>
                )}
                {agruparPorReceptor && erroresAgrupados.length > 0 && (
                  <span style={{ fontSize: 11, color: "#1e40af", marginLeft: 8, fontWeight: 600 }}>
                    · {erroresAgrupados.length} receptor{erroresAgrupados.length !== 1 ? "es" : ""} en esta página
                  </span>
                )}
              </>
            }
            icon="mdi-table-alert"
            action={
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <label style={{
                  display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12,
                  background: agruparPorReceptor ? "#dbeafe" : "#f1f5f9",
                  color: agruparPorReceptor ? "#1e40af" : "#475569",
                  border: agruparPorReceptor ? "1px solid #93c5fd" : "1px solid transparent",
                  padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontWeight: 600,
                  userSelect: "none", height: 30,
                }}>
                  <input
                    type="checkbox"
                    checked={agruparPorReceptor}
                    onChange={e => setAgruparPorReceptor(e.target.checked)}
                    style={{ accentColor: "#3b82f6" }}
                  />
                  <i className="mdi mdi-account-group-outline" />
                  Agrupar por receptor
                </label>
                {agruparPorReceptor && erroresAgrupados.length > 0 && (
                  <div className="btn-group btn-group-sm" role="group">
                    <button type="button" className="btn btn-outline-secondary"
                      onClick={() => expandAll(erroresAgrupados.map(g => g.key))}
                      title="Expandir todos">
                      <i className="mdi mdi-arrow-expand-vertical" />
                    </button>
                    <button type="button" className="btn btn-outline-secondary"
                      onClick={collapseAll} title="Colapsar todos">
                      <i className="mdi mdi-arrow-collapse-vertical" />
                    </button>
                  </div>
                )}
                <select value={pageSize} onChange={e => { const ps = Number(e.target.value); setPageSize(ps); setPage(0); cargarLista(filtrosAplicados, 0) }}
                  style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", height: 30 }}>
                  {PAGE_SIZE_OPTS.map(s => <option key={s} value={s}>{s} por página</option>)}
                </select>
              </div>
            }
          >
            {errorLista && (
              <div style={{
                padding: "10px 14px", background: "#fef2f2", color: "#991b1b",
                border: "1px solid #fecaca", borderRadius: 8, marginBottom: 10,
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <i className="mdi mdi-alert-circle" />
                <span style={{ flex: 1 }}>{errorLista}</span>
                <button type="button" className="btn btn-sm btn-outline-danger"
                  onClick={() => cargarLista(filtrosAplicados, page)}>Reintentar</button>
              </div>
            )}

            {loadingLista && (
              <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
                <Spinner color="primary" size="sm" />
                <span className="ms-2">Cargando errores…</span>
              </div>
            )}

            {!loadingLista && listaErroresFiltrada.length === 0 && !errorLista && (
              <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
                <i className="mdi mdi-emoticon-happy-outline" style={{ fontSize: 32 }} />
                <div className="mt-2">No hay errores con los filtros actuales.</div>
              </div>
            )}

            {!loadingLista && listaErroresFiltrada.length > 0 && (
              <>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1380 }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, textAlign: "center", width: 36 }}>
                          {(() => {
                            const visCufes = listaErroresFiltrada.map(e => e.cufe).filter(Boolean)
                            const allChecked = visCufes.length > 0 && visCufes.every(c => selectedCufes.has(c))
                            const someChecked = !allChecked && visCufes.some(c => selectedCufes.has(c))
                            return (
                              <input type="checkbox" checked={allChecked}
                                ref={el => { if (el) el.indeterminate = someChecked }}
                                onChange={() => toggleGroupCufes(visCufes)}
                                style={{ accentColor: "#3b82f6", cursor: "pointer" }} />
                            )
                          })()}
                        </th>
                        <th style={th}>Fecha</th>
                        <th style={th}>Documento</th>
                        <th style={th}>Sucursal</th>
                        <th style={th}>Destinatario</th>
                        <th style={th}>Receptor</th>
                        <th style={th}>Etapa</th>
                        <th style={th}>Bounce</th>
                        <th style={{ ...th, textAlign: "center" }}>Estado</th>
                        <th style={th}>Mensaje</th>
                        <th style={{ ...th, textAlign: "center" }}>HTTP</th>
                        <th style={{ ...th, textAlign: "center" }}>Retry</th>
                        <th style={{ ...th, textAlign: "center" }}>Origen</th>
                        <th style={{ ...th, textAlign: "center", width: 70 }}>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agruparPorReceptor
                        ? erroresAgrupados.map((grupo) => {
                          const expanded = expandedReceptores.has(grupo.key)
                          const sinNit = grupo.key === "__SIN_NIT__"
                          return (
                            <React.Fragment key={grupo.key}>
                              {/* Fila parent — el receptor */}
                              <tr
                                onClick={() => toggleReceptor(grupo.key)}
                                style={{
                                  background: expanded ? "#eff6ff" : "#f8fafc",
                                  cursor: "pointer",
                                  borderTop: "2px solid #e2e8f0",
                                }}
                              >
                                <td colSpan={14} style={{ padding: "10px 12px" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                                    {(() => {
                                      const groupCufes = grupo.items.map(i => i.cufe).filter(Boolean)
                                      const all = groupCufes.length > 0 && groupCufes.every(c => selectedCufes.has(c))
                                      const some = !all && groupCufes.some(c => selectedCufes.has(c))
                                      return (
                                        <input type="checkbox" checked={all}
                                          ref={el => { if (el) el.indeterminate = some }}
                                          onChange={(e) => { e.stopPropagation(); toggleGroupCufes(groupCufes) }}
                                          onClick={(e) => e.stopPropagation()}
                                          style={{ accentColor: "#3b82f6", cursor: "pointer", flexShrink: 0 }} />
                                      )
                                    })()}
                                    <i className={`mdi mdi-chevron-${expanded ? "down" : "right"}`}
                                      style={{ fontSize: 18, color: "#475569" }} />
                                    <i className="mdi mdi-domain text-primary" />
                                    {sinNit ? (
                                      <span style={{ color: "#94a3b8", fontStyle: "italic" }}>
                                        Sin NIT receptor
                                      </span>
                                    ) : (
                                      <>
                                        <span style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                                          {grupo.nit}
                                        </span>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                                          {grupo.nombre || "(sin nombre)"}
                                        </span>
                                      </>
                                    )}
                                    <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                      <span style={{
                                        padding: "2px 10px", borderRadius: 999,
                                        background: "#fee2e2", color: "#991b1b",
                                        fontSize: 11, fontWeight: 700,
                                      }}>
                                        <i className="mdi mdi-alert-circle me-1" />
                                        {formatNumber(grupo.items.length)} error{grupo.items.length !== 1 ? "es" : ""}
                                      </span>
                                      <span style={{
                                        padding: "2px 10px", borderRadius: 999,
                                        background: "#fef3c7", color: "#92400e",
                                        fontSize: 11, fontWeight: 700,
                                      }}>
                                        <i className="mdi mdi-file-document-outline me-1" />
                                        {formatNumber(grupo.cufesCount)} CUFE{grupo.cufesCount !== 1 ? "s" : ""}
                                      </span>
                                      {Array.from(grupo.etapas).map(et => (
                                        <span key={et} style={{
                                          padding: "1px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                                          background: `${colorEtapa(et)}1a`, color: colorEtapa(et),
                                        }}>{et}</span>
                                      ))}
                                      {!sinNit && (
                                        <button type="button" className="btn btn-sm btn-outline-primary"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            const nuevos = { ...filtrosAvanzados, nit_receptor: grupo.nit }
                                            setFiltrosAvanzados(nuevos)
                                            handleConsultar(nuevos)
                                          }}
                                          title="Filtrar por este receptor (trae sus errores de todo el período)"
                                          style={{ padding: "2px 8px", fontSize: 11 }}>
                                          <i className="mdi mdi-filter-variant" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                              {/* Filas children — los errores del receptor */}
                              {expanded && grupo.items.map((e, idx) => (
                                <tr key={`${grupo.key}-${e.cufe}-${idx}`}
                                  style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                  <td style={{ ...td, textAlign: "center" }}>
                                    <input type="checkbox" checked={selectedCufes.has(e.cufe)}
                                      onChange={() => toggleCufe(e.cufe)}
                                      style={{ accentColor: "#3b82f6", cursor: "pointer" }} />
                                  </td>
                                  <td style={{ ...td, fontFamily: "monospace", fontSize: 11, paddingLeft: 32 }}>
                                    {formatFechaHora(e.event_time)}
                                  </td>
                                  <td style={td}>
                                    <div style={{ fontWeight: 600 }}>{e.prefi_folio || `${e.prefijo || ""}${e.folio || ""}`}</div>
                                    <div style={{ fontSize: 10, color: "#94a3b8" }}>
                                      {e.tipo_documento || ""}
                                      {e.tipopdf && <span style={{ marginLeft: 4, padding: "0 5px", background: "#f1f5f9", borderRadius: 3 }}>{e.tipopdf}</span>}
                                    </div>
                                  </td>
                                  <td style={{ ...td, fontSize: 11 }}>{e.sucursal || "—"}</td>
                                  <td style={{ ...td, wordBreak: "break-all", fontSize: 11 }}>{e.recipient || "—"}</td>
                                  <td style={{ ...td, fontSize: 10, color: "#94a3b8", fontStyle: "italic" }}>
                                    ↑ agrupado
                                  </td>
                                  <td style={td}>
                                    <span style={{
                                      padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                                      background: `${colorEtapa(e.error_stage)}1a`, color: colorEtapa(e.error_stage),
                                    }}>{e.error_stage || "—"}</span>
                                  </td>
                                  <td style={td}>
                                    {e.bounce_type ? (
                                      <span style={{
                                        padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                                        background: bounceConfig(e.bounce_type).bg, color: bounceConfig(e.bounce_type).color,
                                      }}>{bounceConfig(e.bounce_type).label}</span>
                                    ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                                  </td>
                                  <td style={{ ...td, textAlign: "center" }}>
                                    {e.estado_actual_inbox ? (
                                      <span style={{
                                        padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                                        background: String(e.estado_actual_inbox).toLowerCase().includes("definitivo") ? "#fee2e2" : "#f1f5f9",
                                        color: String(e.estado_actual_inbox).toLowerCase().includes("definitivo") ? "#7f1d1d" : "#475569",
                                      }}>{e.estado_actual_inbox}</span>
                                    ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                                  </td>
                                  <td style={{ ...td, maxWidth: 320, fontSize: 11, color: "#7f1d1d" }}
                                    title={e.error_message || ""}>
                                    <div style={{
                                      maxHeight: 60, overflow: "hidden",
                                      textOverflow: "ellipsis", display: "-webkit-box",
                                      WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                      wordBreak: "break-word",
                                    }}>
                                      {e.error_message || "—"}
                                    </div>
                                  </td>
                                  <td style={{ ...td, textAlign: "center", fontSize: 11, fontFamily: "monospace" }}>
                                    {e.http_status_code || <span style={{ color: "#cbd5e1" }}>—</span>}
                                  </td>
                                  <td style={{ ...td, textAlign: "center" }}>
                                    {e.retry_count > 0 ? (
                                      <span style={{
                                        padding: "1px 7px", borderRadius: 999, fontSize: 10, fontWeight: 700,
                                        background: "#fef3c7", color: "#92400e",
                                      }}>×{e.retry_count}</span>
                                    ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                                  </td>
                                  <td style={{ ...td, textAlign: "center", fontSize: 10, color: "#64748b" }}>{e.source || "—"}</td>
                                  <td style={{ ...td, textAlign: "center" }}>
                                    <button type="button" className="btn btn-sm btn-outline-primary"
                                      onClick={() => handleAbrirDetalle(e.cufe)} title="Ver detalle + reenviar">
                                      <i className="mdi mdi-eye-outline" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          )
                        })
                        : listaErroresFiltrada.map((e, i) => (
                          <tr key={`${e.cufe}-${i}`} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                            <td style={{ ...td, textAlign: "center" }}>
                              <input type="checkbox" checked={selectedCufes.has(e.cufe)}
                                onChange={() => toggleCufe(e.cufe)}
                                style={{ accentColor: "#3b82f6", cursor: "pointer" }} />
                            </td>
                            <td style={{ ...td, fontFamily: "monospace", fontSize: 11 }}>
                              {formatFechaHora(e.event_time)}
                            </td>
                            <td style={td}>
                              <div style={{ fontWeight: 600 }}>{e.prefi_folio || `${e.prefijo || ""}${e.folio || ""}`}</div>
                              <div style={{ fontSize: 10, color: "#94a3b8" }}>
                                {e.tipo_documento || ""}
                                {e.tipopdf && <span style={{ marginLeft: 4, padding: "0 5px", background: "#f1f5f9", borderRadius: 3 }}>{e.tipopdf}</span>}
                              </div>
                            </td>
                            <td style={{ ...td, fontSize: 11 }}>{e.sucursal || "—"}</td>
                            <td style={{ ...td, wordBreak: "break-all", fontSize: 11 }}>{e.recipient || "—"}</td>
                            <td style={td}>
                              <div style={{ fontFamily: "monospace", fontSize: 11 }}>{e.nit_receptor || "—"}</div>
                              <div style={{ fontSize: 10, color: "#94a3b8" }}>{e.nombre_receptor || ""}</div>
                            </td>
                            <td style={td}>
                              <span style={{
                                padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                                background: `${colorEtapa(e.error_stage)}1a`, color: colorEtapa(e.error_stage),
                              }}>{e.error_stage || "—"}</span>
                            </td>
                            <td style={td}>
                              {e.bounce_type ? (
                                <span style={{
                                  padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                                  background: bounceConfig(e.bounce_type).bg, color: bounceConfig(e.bounce_type).color,
                                }}>{bounceConfig(e.bounce_type).label}</span>
                              ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                            </td>
                            <td style={{ ...td, textAlign: "center" }}>
                              {e.estado_actual_inbox ? (
                                <span style={{
                                  padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                                  background: String(e.estado_actual_inbox).toLowerCase().includes("definitivo") ? "#fee2e2" : "#f1f5f9",
                                  color: String(e.estado_actual_inbox).toLowerCase().includes("definitivo") ? "#7f1d1d" : "#475569",
                                }}>{e.estado_actual_inbox}</span>
                              ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                            </td>
                            <td style={{ ...td, maxWidth: 320, fontSize: 11, color: "#7f1d1d" }}
                              title={e.error_message || ""}>
                              <div style={{
                                maxHeight: 60, overflow: "hidden",
                                textOverflow: "ellipsis", display: "-webkit-box",
                                WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                wordBreak: "break-word",
                              }}>
                                {e.error_message || "—"}
                              </div>
                            </td>
                            <td style={{ ...td, textAlign: "center", fontSize: 11, fontFamily: "monospace" }}>
                              {e.http_status_code || <span style={{ color: "#cbd5e1" }}>—</span>}
                            </td>
                            <td style={{ ...td, textAlign: "center" }}>
                              {e.retry_count > 0 ? (
                                <span style={{
                                  padding: "1px 7px", borderRadius: 999, fontSize: 10, fontWeight: 700,
                                  background: "#fef3c7", color: "#92400e",
                                }}>×{e.retry_count}</span>
                              ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                            </td>
                            <td style={{ ...td, textAlign: "center", fontSize: 10, color: "#64748b" }}>{e.source || "—"}</td>
                            <td style={{ ...td, textAlign: "center" }}>
                              <button type="button" className="btn btn-sm btn-outline-primary"
                                onClick={() => handleAbrirDetalle(e.cufe)} title="Ver detalle + reenviar">
                                <i className="mdi mdi-eye-outline" />
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* Paginación */}
                <div style={{
                  padding: "10px 0 0", display: "flex", justifyContent: "space-between",
                  alignItems: "center", flexWrap: "wrap", gap: 8, fontSize: 13, color: "#475569",
                }}>
                  <span>
                    Mostrando {listaMeta.total === 0 ? 0 : listaMeta.offset + 1}—
                    {Math.min(listaMeta.offset + listaErroresFiltrada.length, listaMeta.total)} de {formatNumber(listaMeta.total)}
                    {omitidosSinNit > 0 && (
                      <span style={{ color: "#94a3b8", marginLeft: 6 }}>
                        ({omitidosSinNit} sin NIT receptor ocultos en esta página)
                      </span>
                    )}
                  </span>
                  {totalPages > 1 && (
                    <div style={{ display: "flex", gap: 4 }}>
                      <button type="button" className="btn btn-sm btn-outline-secondary"
                        disabled={page === 0} onClick={() => handlePageChange(0)}>
                        <i className="mdi mdi-chevron-double-left" />
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-secondary"
                        disabled={page === 0} onClick={() => handlePageChange(Math.max(0, page - 1))}>
                        <i className="mdi mdi-chevron-left" />
                      </button>
                      <span style={{ padding: "0 10px", display: "inline-flex", alignItems: "center", fontWeight: 600 }}>
                        {page + 1} / {totalPages}
                      </span>
                      <button type="button" className="btn btn-sm btn-outline-secondary"
                        disabled={page >= totalPages - 1} onClick={() => handlePageChange(page + 1)}>
                        <i className="mdi mdi-chevron-right" />
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-secondary"
                        disabled={page >= totalPages - 1} onClick={() => handlePageChange(totalPages - 1)}>
                        <i className="mdi mdi-chevron-double-right" />
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </SectionCard>
        </>
      )}

      {/* ───── Barra flotante de selección + reenvío masivo ───── */}
      {selectedCufes.size > 0 && (
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1050,
          background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
          color: "#fff", padding: "12px 24px",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 14, flexWrap: "wrap",
          boxShadow: "0 -6px 24px rgba(0,0,0,0.28)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <i className="mdi mdi-checkbox-multiple-marked-outline" style={{ fontSize: 20, color: "#60a5fa" }} />
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              {selectedCufes.size} factura{selectedCufes.size > 1 ? "s" : ""} seleccionada{selectedCufes.size > 1 ? "s" : ""}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input type="email" placeholder="Nuevo destinatario (opcional, deja vacío para el original)"
              value={bulkMail}
              onChange={e => setBulkMail(e.target.value)}
              disabled={bulkSending}
              style={{
                fontSize: 13, padding: "6px 12px", height: 34, borderRadius: 6,
                border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.1)",
                color: "#fff", minWidth: 320,
              }} />
          </div>
          {bulkSending ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 280 }}>
              <Spinner size="sm" color="light" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, marginBottom: 4 }}>
                  Enviando {bulkProgress.current} de {bulkProgress.total}…
                  {bulkProgress.ok > 0 && <span style={{ color: "#86efac", marginLeft: 6 }}>✓ {bulkProgress.ok}</span>}
                  {bulkProgress.fail > 0 && <span style={{ color: "#fca5a5", marginLeft: 6 }}>✗ {bulkProgress.fail}</span>}
                </div>
                <div style={{ background: "rgba(255,255,255,0.2)", borderRadius: 4, height: 6 }}>
                  <div style={{
                    background: "#3b82f6", borderRadius: 4, height: 6,
                    width: `${bulkProgress.total > 0 ? (bulkProgress.current / bulkProgress.total) * 100 : 0}%`,
                    transition: "width 0.3s",
                  }} />
                </div>
              </div>
            </div>
          ) : (
            <>
              <Button color="primary" onClick={handleBulkReenviar}
                style={{ borderRadius: 8, fontWeight: 700, padding: "7px 18px" }}>
                <i className="fas fa-paper-plane me-2" />
                Reenviar ({selectedCufes.size})
              </Button>
              <button type="button" onClick={clearSelection}
                style={{
                  background: "transparent", border: "1px solid rgba(255,255,255,0.3)",
                  color: "#cbd5e1", borderRadius: 8, padding: "6px 14px", fontSize: 13, cursor: "pointer",
                }}>
                <i className="fas fa-times me-1" /> Limpiar
              </button>
            </>
          )}
        </div>
      )}

      {/* ───── Modal resumen post-reenvío ───── */}
      {bulkSummary && (
        <div onClick={() => { setBulkSummary(null); clearSelection() }} style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
          zIndex: 1060, display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "#fff", borderRadius: 14, padding: 22, maxWidth: 640, width: "100%",
            maxHeight: "80vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <i className={`mdi mdi-${bulkSummary.fail === 0 ? "check-circle" : "alert-circle"}`}
                style={{ fontSize: 28, color: bulkSummary.fail === 0 ? "#16a34a" : "#f59e0b" }} />
              <h5 style={{ margin: 0, fontWeight: 700 }}>Reenvío completado</h5>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
              <div style={{ flex: 1, minWidth: 120, background: "#dcfce7", padding: 12, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: "#166534" }}>{bulkSummary.ok}</div>
                <div style={{ fontSize: 11, color: "#166534", fontWeight: 700, textTransform: "uppercase" }}>Exitosos</div>
              </div>
              <div style={{ flex: 1, minWidth: 120, background: bulkSummary.fail > 0 ? "#fee2e2" : "#f1f5f9", padding: 12, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: bulkSummary.fail > 0 ? "#991b1b" : "#475569" }}>{bulkSummary.fail}</div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: bulkSummary.fail > 0 ? "#991b1b" : "#475569" }}>Fallidos</div>
              </div>
              {bulkSummary.legacy > 0 && (
                <div style={{ flex: 1, minWidth: 120, background: "#fef3c7", padding: 12, borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: "#92400e" }}>{bulkSummary.legacy}</div>
                  <div style={{ fontSize: 11, color: "#92400e", fontWeight: 700, textTransform: "uppercase" }}>Vía legacy</div>
                </div>
              )}
            </div>
            {bulkSummary.errores.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#991b1b", marginBottom: 6 }}>
                  Errores ({bulkSummary.errores.length}):
                </div>
                <div style={{ maxHeight: 240, overflowY: "auto", background: "#fef2f2", borderRadius: 8, padding: 10, fontSize: 11 }}>
                  {bulkSummary.errores.map((er, i) => (
                    <div key={i} style={{ paddingBottom: 6, marginBottom: 6, borderBottom: "1px solid #fecaca" }}>
                      <div style={{ fontFamily: "monospace", fontSize: 10, color: "#475569", wordBreak: "break-all" }}>{er.cufe}</div>
                      <div style={{ color: "#7f1d1d" }}>{er.msg}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ textAlign: "right", marginTop: 14 }}>
              <Button color="primary" onClick={() => { setBulkSummary(null); clearSelection() }}>
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      <DrawerDetalle
        abierto={drawerAbierto}
        cufe={cufeActivo}
        onCerrar={() => setDrawerAbierto(false)}
      />
    </div>
  )
}

ErroresPorEmisor.propTypes = {
  empresaSeleccionada: PropTypes.object,
  user: PropTypes.object,
  setBreadcrumbItems: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  empresaSeleccionada: state.Login.empresaSeleccionada,
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(ErroresPorEmisor)
